import {FormField} from '../../../types/form-builder.types';
import {cn, useLanguage} from '@shondalai/ui-commons';
import {Frown, Meh, Smile, Laugh, ThumbsDown, ThumbsUp} from 'lucide-react';

interface EmojiSentimentFieldProps {
  field: FormField;
  value: any;
  onChange: (value: any) => void;
  error?: string;
}

export function EmojiSentimentField({ field, value, onChange }: EmojiSentimentFieldProps) {
  const config = field.config;
  const { t } = useLanguage();
  const emojiSet = config.emojiSet || 'satisfaction';

  const emojiOptions = {
    satisfaction: [
      { value: '1', Icon: Frown, label: t('COM_EASYFORMS_VERY_DISSATISFIED', 'Very Dissatisfied') },
      { value: '2', Icon: Frown, label: t('COM_EASYFORMS_DISSATISFIED', 'Dissatisfied') },
      { value: '3', Icon: Meh,   label: t('COM_EASYFORMS_NEUTRAL', 'Neutral') },
      { value: '4', Icon: Smile, label: t('COM_EASYFORMS_SATISFIED', 'Satisfied') },
      { value: '5', Icon: Laugh, label: t('COM_EASYFORMS_VERY_SATISFIED', 'Very Satisfied') },
    ],
    emotions: [
      { value: '1', Icon: Frown, label: t('COM_EASYFORMS_SAD', 'Sad') },
      { value: '2', Icon: Frown, label: t('COM_EASYFORMS_CONCERNED', 'Concerned') },
      { value: '3', Icon: Meh,   label: t('COM_EASYFORMS_OKAY', 'Okay') },
      { value: '4', Icon: Smile, label: t('COM_EASYFORMS_HAPPY', 'Happy') },
      { value: '5', Icon: Laugh, label: t('COM_EASYFORMS_LOVE_IT', 'Love It') },
    ],
    thumbs: [
      { value: 'down', Icon: ThumbsDown, label: t('COM_EASYFORMS_THUMBS_DOWN', 'Thumbs Down') },
      { value: 'up',   Icon: ThumbsUp,   label: t('COM_EASYFORMS_THUMBS_UP', 'Thumbs Up') },
    ],
    numbers: [
      { value: '1', label: '1' },
      { value: '2', label: '2' },
      { value: '3', label: '3' },
      { value: '4', label: '4' },
      { value: '5', label: '5' },
    ],
  } as const;

  const options = (emojiOptions as any)[emojiSet] || (emojiOptions as any).satisfaction;

  return (
    <div className="flex justify-center gap-2 md:gap-3">
      {options.map((option: any, index: number) => {
        const isSelected = value === option.value;

        return (
          <button
            key={option.id || option.value || `emoji-${index}`}
            type="button"
            onClick={() => onChange(option.value)}
            className={cn(
              "flex flex-col items-center gap-2 p-3 md:p-4 rounded-lg min-w-[60px]",
              "border-2 transition-all duration-200 hover:scale-110",
              "focus:outline-none focus:ring-2 focus:ring-offset-1"
            )}
            style={isSelected ? {
              borderColor: 'var(--ef-accent, #3B82F6)',
              background: 'var(--ef-surface, #F8FAFC)',
              transform: 'scale(1.05)',
              '--tw-ring-color': 'var(--ef-accent, #3B82F6)'
            } as React.CSSProperties : {
              borderColor: 'var(--ef-border, #E2E8F0)',
              background: 'var(--ef-background, #FFFFFF)'
            }}
          >
            {option.Icon ? (
              <option.Icon
                className="w-8 h-8 md:w-9 md:h-9 transition-colors"
                style={{
                  color: isSelected ? 'var(--ef-accent, #3B82F6)' : 'var(--ef-textSecondary, #64748B)',
                  opacity: isSelected ? 1 : 0.5
                }}
                strokeWidth={1.5}
              />
            ) : (
              <span
                className="text-xl md:text-2xl font-semibold"
                style={{ color: 'var(--ef-text, #1E293B)' }}
              >
                {option.label}
              </span>
            )}
            {config.showLabels && option.label && option.Icon && (
              <span
                className="text-[11px] font-light text-center leading-tight"
                style={{ color: 'var(--ef-textSecondary, #64748B)', opacity: 0.7 }}
              >
                {option.label}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
